import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { leadCreate } from "@shared/schemas/leads";
import { normalizePhone } from "@shared/phone";
import { errorMessage } from "@web/lib/api";
import { parseGuestCount } from "@shared/guestCount";
import { validateLead } from "@shared/validation/leads";
import { validateContact } from "@shared/validation/contacts";
import { evaluation, parseInput } from "@shared/validation/issues";
import { useFormValidation } from "@web/lib/formValidation";
import { contactsQuery, eventTypesQuery, leadSourcesQuery, venuesQuery } from "@web/lib/queries";
import { leadApi } from "@web/lib/queries-leads";
import { BottomSheet, Button, FormGrid, Inline, Picker, Stack, Text, TextArea, TextField, Toggle, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

export type NewLeadSheetProps = { onClose: () => void; initialVenueId?: string | null };

/** `+ Lead` sheet (SPEC §6.1 item 3): contact, venue, event, source, SMS consent, notes → POST /api/leads (dedupes server-side). */
export function NewLeadSheet({ onClose, initialVenueId }: NewLeadSheetProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const venues = useQuery(venuesQuery());
  const eventTypes = useQuery(eventTypesQuery());
  const sources = useQuery(leadSourcesQuery());
  const [f, setF] = useState({ firstName: "", lastName: "", companyName: "", title: "", phone: "", email: "", venueId: initialVenueId ?? "", eventTypeId: "", eventName: "", eventDate: "", eventDateFlexible: false, guests: "", sourceId: "", smsConsent: false, notes: "" });
  const [initialDraft] = useState(() => JSON.stringify(f));
  const saved = useRef(false);
  const matches = useQuery(contactsQuery(f.email.trim() || f.phone.trim()));
  const existing = matches.data?.find((contact) => (f.email.trim() && contact.email === f.email.trim().toLowerCase()) || (normalizePhone(f.phone) && normalizePhone(contact.phone) === normalizePhone(f.phone)));
  const [error, setError] = useState<string | null>(null);
  const guestInput = useRef<HTMLInputElement>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => validation.edit(() => { setF((x) => ({ ...x, [k]: v })); setError(null); });
  const effectiveVenue = f.venueId || (venues.data?.length === 1 ? venues.data[0].id : "");
  const input = {
    contact: { firstName: f.firstName, lastName: f.lastName, companyName: f.companyName || null, title: f.title || null, phone: f.phone.trim() || null, email: f.email.trim() || null, smsConsent: f.smsConsent },
    venueId: effectiveVenue || null, eventTypeId: f.eventTypeId || null, eventName: f.eventName || null, eventDate: f.eventDate || null, eventDateFlexible: f.eventDateFlexible,
    guestCount: f.guests.trim() || null, sourceId: f.sourceId || null, notes: f.notes.trim() || null,
  };
  const parsedInput = parseInput(leadCreate, input, { type: "lead" }, "lead.save"), guests = parseGuestCount(input.guestCount);
  const local = validateLead({ ...input, contact: undefined, guestCount: guests?.min ?? null, guestCountMin: guests?.min ?? null, guestCountMax: guests?.max ?? null }, { action: "lead.save", primaryContact: input.contact, manualNewContact: true });
  const contactFindings = validateContact(input.contact).issues.map(issue => ({ ...issue, fields: issue.fields.map(field => `contact.${field}`) }));
  const validation = useFormValidation(evaluation([...parsedInput.issues, ...local.issues, ...contactFindings], false));
  const { busy, field } = validation;

  async function save() {
    if (!parsedInput.success) {
      if (parsedInput.issues.some(issue => issue.fields.includes("guestCount"))) guestInput.current?.focus();
      return;
    }
    if (!validation.begin("lead.save")) return;
    setError(null);
    try {
      const r = await leadApi.createLead(parsedInput.data);
      if (r.deduped) toast.show("Matched an existing lead");
      else toast.success("Lead added");
      saved.current = true;
      onClose();
      navigate({ to: "/leads/$id", params: { id: r.id } });
    } catch (e) {
      validation.reject(e);
      setError(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => validation.edit(onClose)}
      title="New lead"
      footer={
        <Button loading={busy} onClick={save}>
          Save lead
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={JSON.stringify(f) !== initialDraft} pending={busy} canLeave={() => saved.current} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {error && <Text variant="callout" tone="danger">{error}</Text>}
        <Inline gap={2} align="start">
          <TextField label="First name" className="flex-1" autoComplete="off" value={f.firstName} onChange={(e) => set("firstName", e.target.value)} autoFocus {...field("contact.firstName")} />
          <TextField label="Last name" className="flex-1" autoComplete="off" value={f.lastName} onChange={(e) => set("lastName", e.target.value)} {...field("contact.lastName")} />
        </Inline>
        <FormGrid>
          <TextField label="Company" autoComplete="organization" value={f.companyName} onChange={(e) => set("companyName", e.target.value)} {...field("contact.companyName")} />
          <TextField label="Job title" autoComplete="organization-title" value={f.title} onChange={(e) => set("title", e.target.value)} {...field("contact.title")} />
        </FormGrid>
        <FormGrid>
        <TextField label="Phone" type="tel" inputMode="tel" autoComplete="off" value={f.phone} {...field("contact.phone")} onChange={(e) => set("phone", e.target.value)} />
        <TextField label="Email" type="email" inputMode="email" autoCapitalize="none" autoComplete="off" value={f.email} {...field("contact.email")} onChange={(e) => set("email", e.target.value)} />
        </FormGrid>
        {existing && <Text variant="callout" tone="secondary">This contact already exists. Saving preserves their contact details and adds this inquiry to their recent open lead when one is available.</Text>}
        <Picker label="Venue" placeholder="Not sure yet" value={effectiveVenue} onChange={(v) => set("venueId", v)} options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))} {...field("venueId")} />
        <TextField label="Event name" placeholder="e.g. ABC Holiday Party" value={f.eventName} onChange={(e) => set("eventName", e.target.value)} {...field("eventName")} />
        <Inline gap={2} align="start">
          <Picker label="Event type" placeholder="—" className="flex-1" value={f.eventTypeId} onChange={(v) => set("eventTypeId", v)} options={(eventTypes.data ?? []).map((t) => ({ value: t.id, label: t.name }))} {...field("eventTypeId")} />
          <TextField label="Guests" placeholder="100–150" hint="A number, range or 150+" inputRef={guestInput} className="flex-1" value={f.guests} onChange={(e) => set("guests", e.target.value)} {...field("guestCount")} />
        </Inline>
        <TextField label="Event date" type="date" value={f.eventDate} {...field("eventDate")} onChange={(e) => set("eventDate", e.target.value)} />
        <Toggle label="Date is flexible" checked={f.eventDateFlexible} disabled={busy} onChange={(v) => set("eventDateFlexible", v)} />
        <Picker label="Source" placeholder="—" value={f.sourceId} onChange={(v) => set("sourceId", v)} options={(sources.data ?? []).map((s) => ({ value: s.id, label: s.name }))} {...field("sourceId")} />
        <Toggle label="SMS consent" description="They agreed to receive texts" checked={f.smsConsent} disabled={busy} onChange={(v) => set("smsConsent", v)} />
        <TextArea label="Notes" placeholder="What they asked for" value={f.notes} onChange={(e) => set("notes", e.target.value)} {...field("notes")} />
      </Stack>
    </BottomSheet>
  );
}
